/**
 * 測試資料 builder。
 *
 * 設計原則：
 * 1. 每個 `makeX()` 都回傳「完整合法」的實體，呼叫端只需覆寫關心的欄位。
 * 2. 所有 builder 都是純函式、無隱藏全域狀態，唯一的例外是 `nextUid()` 計數器，
 *    可用 `resetFixtureCounters()` 歸零以確保測試可重現。
 * 3. 這裡不執行任何排程計算；start/finish 等欄位只是佔位值。
 */

import {
  isoDate,
  isoDateTime,
  minutes,
  money,
  percent,
  ratePerMinute,
  units,
  type AssignmentId,
  type CalendarId,
  type DependencyId,
  type ISODateTime,
  type ProjectId,
  type ResourceId,
  type TaskId,
} from '../domain/branded.js';
import type { Task } from '../domain/task.js';
import type { Dependency } from '../domain/dependency.js';
import type { Resource } from '../domain/resource.js';
import type { Assignment } from '../domain/assignment.js';
import type {
  Calendar,
  NonEmptyWorkingTimes,
  WeekDays,
  WeekDaySpec,
} from '../domain/calendar.js';
import type { Project } from '../domain/project.js';

let uidCounter = 0;
const nextUid = (): number => {
  uidCounter += 1;
  return uidCounter;
};

/** 將 uid 計數器歸零，讓測試結果可重現。 */
export const resetFixtureCounters = (): void => {
  uidCounter = 0;
};

// ---------------------------------------------------------------------------
// 常數
// ---------------------------------------------------------------------------

export const DEFAULT_CALENDAR_ID: CalendarId = 'cal-standard' as CalendarId;
export const DEFAULT_PROJECT_START: ISODateTime = '2026-01-05T08:00:00' as ISODateTime;

/** 08:00–12:00 與 13:00–17:00，合計 480 分鐘／日。 */
export const STANDARD_WORKING_TIMES: NonEmptyWorkingTimes = [
  { start: minutes(8 * 60), end: minutes(12 * 60) },
  { start: minutes(13 * 60), end: minutes(17 * 60) },
];

/** 明確的非工作日（覆寫 base calendar）。 */
export const NON_WORKING_DAY = { working: false } as const satisfies WeekDaySpec;

/** 建立「該日工作」的 weekday 設定。 */
export const workingDay = (
  times: NonEmptyWorkingTimes = STANDARD_WORKING_TIMES,
): WeekDaySpec => ({ working: true, times });

/** 週一至週五 8 小時、週末明確休息。 */
export const STANDARD_WEEK_DAYS: WeekDays = [
  NON_WORKING_DAY,
  workingDay(),
  workingDay(),
  workingDay(),
  workingDay(),
  workingDay(),
  NON_WORKING_DAY,
];

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

export function makeCalendar(overrides: Partial<Calendar> = {}): Calendar {
  return {
    id: DEFAULT_CALENDAR_ID,
    name: 'Standard',
    baseCalendarId: null,
    weekDays: STANDARD_WEEK_DAYS,
    exceptions: [],
    ...overrides,
  };
}

/** 產生一個國定假日例外。 */
export function makeHoliday(
  from: string,
  to: string = from,
  name = 'Holiday',
): Calendar['exceptions'][number] {
  return {
    id: `exc-${from}`,
    name,
    from: isoDate(from),
    to: isoDate(to),
    working: false,
    workingTimes: [],
  };
}

export function makeTask(overrides: Partial<Task> = {}): Task {
  const uid = overrides.uid ?? nextUid();
  const start = overrides.start ?? DEFAULT_PROJECT_START;
  const finish = overrides.finish ?? isoDateTime('2026-01-05T17:00:00');
  return {
    id: `t${uid}` as TaskId,
    uid,
    wbs: String(uid),
    name: `Task ${uid}`,
    outlineLevel: 1,
    parentId: null,
    durationMinutes: minutes(480),
    work: minutes(480),
    start,
    finish,
    earlyStart: start,
    earlyFinish: finish,
    lateStart: start,
    lateFinish: finish,
    totalFloat: minutes(0),
    freeFloat: minutes(0),
    isCritical: false,
    isSummary: false,
    isMilestone: false,
    isManuallyScheduled: false,
    taskType: 'FixedUnits',
    effortDriven: false,
    constraintType: 'ASAP',
    constraintDate: null,
    deadline: null,
    percentComplete: percent(0),
    percentWorkComplete: percent(0),
    actualStart: null,
    actualFinish: null,
    priority: 500,
    calendarId: null,
    ignoreResourceCalendar: false,
    fixedCost: money(0),
    fixedCostAccrual: 'Prorated',
    notes: '',
    ...overrides,
  };
}

/** 里程碑：工期 0、無工時。 */
export function makeMilestone(overrides: Partial<Task> = {}): Task {
  return makeTask({
    durationMinutes: minutes(0),
    work: minutes(0),
    isMilestone: true,
    ...overrides,
  });
}

/** 摘要任務。子任務請自行以 `parentId` 指向此任務的 id。 */
export function makeSummaryTask(overrides: Partial<Task> = {}): Task {
  return makeTask({ isSummary: true, ...overrides });
}

export function makeDependency(
  predecessorId: string,
  successorId: string,
  overrides: Partial<Dependency> = {},
): Dependency {
  return {
    id: `d-${predecessorId}-${successorId}` as DependencyId,
    predecessorId: predecessorId as TaskId,
    successorId: successorId as TaskId,
    type: 'FS',
    lagMinutes: minutes(0),
    ...overrides,
  };
}

export function makeResource(overrides: Partial<Resource> = {}): Resource {
  const uid = overrides.uid ?? nextUid();
  return {
    id: `r${uid}` as ResourceId,
    uid,
    name: `Resource ${uid}`,
    type: 'Work',
    initials: `R${uid}`,
    group: '',
    maxUnits: units(1),
    standardRate: ratePerMinute(1),
    overtimeRate: ratePerMinute(1.5),
    costPerUse: money(0),
    accrueAt: 'Prorated',
    calendarId: null,
    materialLabel: null,
    availability: [],
    ...overrides,
  };
}

export function makeAssignment(
  taskId: string,
  resourceId: string,
  overrides: Partial<Assignment> = {},
): Assignment {
  const work = overrides.work ?? minutes(480);
  return {
    id: `a-${taskId}-${resourceId}` as AssignmentId,
    taskId: taskId as TaskId,
    resourceId: resourceId as ResourceId,
    units: units(1),
    work,
    actualWork: minutes(0),
    remainingWork: work,
    start: DEFAULT_PROJECT_START,
    finish: isoDateTime('2026-01-05T17:00:00'),
    cost: money(0),
    actualCost: money(0),
    timephased: null,
    ...overrides,
  };
}

export function makeProject(overrides: Partial<Project> = {}): Project {
  return {
    id: 'p1' as ProjectId,
    name: 'Test Project',
    startDate: DEFAULT_PROJECT_START,
    finishDate: isoDateTime('2026-12-31T17:00:00'),
    scheduleFrom: 'ProjectStart',
    statusDate: null,
    defaultCalendarId: DEFAULT_CALENDAR_ID,
    currency: 'TWD',
    tasks: [],
    dependencies: [],
    resources: [],
    assignments: [],
    calendars: [makeCalendar()],
    baselines: [],
    customFields: [],
    ...overrides,
  };
}

// ---------------------------------------------------------------------------
// 組合式情境
// ---------------------------------------------------------------------------

/**
 * 產生一條 n 個任務、以 FS 串接的線性鏈。
 * 回傳的 project 已含 tasks 與 dependencies，但尚未排程。
 */
export function makeLinearChainProject(taskCount: number): Project {
  const tasks: Task[] = [];
  const dependencies: Dependency[] = [];
  for (let i = 0; i < taskCount; i += 1) {
    const task = makeTask({ name: `Chain ${i + 1}` });
    tasks.push(task);
    const previous = tasks[i - 1];
    if (previous !== undefined) {
      dependencies.push(makeDependency(previous.id, task.id));
    }
  }
  return makeProject({ tasks, dependencies });
}
